<template>
  <Teleport to="body">
    <template v-if="store.isActive && step">
      <div
        v-if="spot"
        class="help-tour-spot pointer-events-none fixed z-[126] rounded-xl text-light-accent dark:text-dark-accent"
        :style="{ top: `${spot.top}px`, left: `${spot.left}px`, width: `${spot.width}px`, height: `${spot.height}px` }"
        aria-hidden="true"
      />
      <div v-else-if="waiting" class="help-tour-dim pointer-events-none fixed inset-0 z-[126]" aria-hidden="true" />

      <Transition name="help-tour-card" appear>
        <section
          :key="store.activeKey"
          ref="cardRef"
          role="dialog"
          aria-modal="false"
          :aria-labelledby="titleId"
          :aria-describedby="textId"
          class="fixed z-[127] rounded-2xl border border-light-border bg-white p-4 shadow-2xl dark:border-dark-border dark:bg-dark-background"
          :class="isMobile ? 'inset-x-3' : 'w-[340px]'"
          :style="{ ...cardStyle, visibility: hasPosition ? 'visible' : 'hidden' }"
          @keydown="onCardKeydown"
        >
          <div class="flex items-start justify-between gap-3">
            <p class="text-[11px] font-semibold uppercase tracking-wider text-light-accent dark:text-dark-accent">
              Paso {{ store.stepIndex + 1 }} de {{ store.steps.length }}
            </p>
            <button
              type="button"
              class="-mr-1.5 -mt-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-light-text-secondary transition-colors hover:bg-light-border/40 hover:text-light-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-light-accent/40 dark:text-dark-text-secondary dark:hover:bg-dark-border/40 dark:hover:text-dark-text-primary dark:focus-visible:ring-dark-accent/40"
              aria-label="Cerrar guía"
              @click="skip"
            >
              <XMarkIcon class="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div aria-live="polite">
            <h2 :id="titleId" class="mt-0.5 text-base font-bold text-light-text-primary dark:text-dark-text-primary">
              {{ step.title }}
            </h2>
            <p :id="textId" class="mt-1 text-sm leading-relaxed text-light-text-secondary dark:text-dark-text-secondary">
              {{ step.text }}
            </p>
            <p
              v-if="waiting && step.waitText"
              class="mt-2.5 flex items-start gap-1.5 rounded-lg bg-light-accent/10 px-2.5 py-2 text-xs font-medium text-light-accent dark:bg-dark-accent/15 dark:text-dark-accent"
            >
              <ClockIcon class="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {{ step.waitText }}
            </p>
          </div>

          <div class="mt-3 flex gap-1" aria-hidden="true">
            <span
              v-for="(_, index) in store.steps"
              :key="index"
              class="h-1 flex-1 rounded-full transition-colors duration-200"
              :class="index <= store.stepIndex ? 'bg-light-accent dark:bg-dark-accent' : 'bg-light-border dark:bg-dark-border'"
            />
          </div>

          <div class="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              class="rounded-md px-1 py-1.5 text-sm font-medium text-light-text-secondary transition-colors hover:text-light-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-light-accent/40 dark:text-dark-text-secondary dark:hover:text-dark-text-primary"
              @click="skip"
            >
              Saltar guía
            </button>
            <div class="flex items-center gap-2">
              <button
                v-if="store.stepIndex > 0"
                type="button"
                class="rounded-lg border border-light-border px-3 py-2 text-sm font-semibold text-light-text-primary transition-colors hover:bg-light-border/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-light-accent/40 dark:border-dark-border dark:text-dark-text-primary dark:hover:bg-dark-border/30"
                @click="store.prev()"
              >
                Atrás
              </button>
              <button
                ref="nextBtnRef"
                type="button"
                class="rounded-lg bg-light-accent px-4 py-2 text-sm font-semibold text-light-cta-text transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-light-accent/40 focus-visible:ring-offset-2 dark:bg-dark-accent dark:text-dark-cta-text dark:focus-visible:ring-offset-dark-background"
                @click="store.next()"
              >
                {{ store.isLast ? 'Terminar' : 'Siguiente' }}
              </button>
            </div>
          </div>

          <div
            v-if="store.autoStarted"
            class="mt-3 border-t border-light-border pt-2.5 text-xs dark:border-dark-border"
          >
            <button
              type="button"
              class="text-light-text-secondary hover:text-light-text-primary hover:underline dark:text-dark-text-secondary dark:hover:text-dark-text-primary"
              @click="turnOffGuides"
            >
              No mostrar más guías
            </button>
          </div>
        </section>
      </Transition>
    </template>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { XMarkIcon, ClockIcon } from '@heroicons/vue/24/outline'
import { useTourStore } from '@/stores/tour'

const store = useTourStore()

const step = computed(() => store.step)

const uid = Math.random().toString(36).slice(2, 8)
const titleId = `help-tour-title-${uid}`
const textId = `help-tour-text-${uid}`

const SPOT_PAD = 6
const EDGE = 12
const GAP = 12
const MOBILE_MAX = 639
const OPTIONAL_GRACE_FRAMES = 4

const cardRef = ref(null)
const nextBtnRef = ref(null)
const spot = ref(null)
const waiting = ref(false)
const isMobile = ref(false)
const cardStyle = ref({})
const hasPosition = computed(() => Object.keys(cardStyle.value).length > 0)

let raf = 0
let scrollRaf = 0
let cardSize = { w: 340, h: 200 }
let scrolledFor = ''
let stepFrames = 0
let returnFocusEl = null

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true

function isVisible(el) {
  if (!el || !el.isConnected || !el.getClientRects().length) return false
  return getComputedStyle(el).visibility !== 'hidden'
}

function findRoot() {
  if (!store.activeKey) return null
  for (const node of document.querySelectorAll(`[data-tour-root="${store.activeKey}"]`)) {
    if (isVisible(node)) return node
  }
  return null
}

function findTarget(root, name) {
  if (!root || !name) return null
  for (const node of root.querySelectorAll(`[data-tour="${name}"]`)) {
    if (isVisible(node)) return node
  }
  return null
}

function keyboardInset() {
  const vv = window.visualViewport
  if (!vv) return 0
  return Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), Math.max(min, max))
}

function scrollParentOf(el) {
  let node = el.parentElement
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node)
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight + 1) return node
    node = node.parentElement
  }
  return null
}

function animateScroll(container, top) {
  cancelAnimationFrame(scrollRaf)
  const start = container.scrollTop
  const distance = top - start
  if (Math.abs(distance) < 2 || prefersReducedMotion()) {
    container.scrollTop = top
    return
  }
  const duration = 450
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
  const startedAt = performance.now()
  const tick = (now) => {
    const progress = Math.min((now - startedAt) / duration, 1)
    container.scrollTop = start + distance * ease(progress)
    if (progress < 1) scrollRaf = requestAnimationFrame(tick)
  }
  scrollRaf = requestAnimationFrame(tick)
}

function bringIntoView(el) {
  const container = scrollParentOf(el)
  if (!container) return
  const box = container.getBoundingClientRect()
  const rect = el.getBoundingClientRect()
  const reserved = isMobile.value ? cardSize.h + EDGE * 2 + keyboardInset() : 0
  const visibleTop = box.top + 8
  const visibleBottom = Math.min(box.bottom, window.innerHeight - reserved) - 8
  if (rect.top >= visibleTop && rect.bottom <= visibleBottom) return
  const available = visibleBottom - visibleTop
  const offset = rect.height >= available ? 0 : (available - rect.height) / 3
  const target = container.scrollTop + (rect.top - visibleTop) - offset
  animateScroll(container, clamp(target, 0, container.scrollHeight - container.clientHeight))
}

function placeCard(sp) {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const { w, h } = cardSize

  if (isMobile.value) {
    const kb = keyboardInset()
    const bottomZoneTop = vh - kb - h - EDGE * 2
    const spotInBottomZone = sp && sp.top + sp.height > bottomZoneTop
    if (spotInBottomZone && sp.top > h + EDGE * 2) {
      return { top: `calc(${EDGE}px + env(safe-area-inset-top, 0px))` }
    }
    return { bottom: `calc(${EDGE + kb}px + env(safe-area-inset-bottom, 0px))` }
  }

  if (!sp) {
    return { top: `${Math.round(vh - h - EDGE * 2)}px`, left: `${Math.round((vw - w) / 2)}px` }
  }

  let top
  let left
  const below = sp.top + sp.height + GAP
  const above = sp.top - GAP - h
  if (below + h <= vh - EDGE) {
    top = below
    left = sp.left
  } else if (above >= EDGE) {
    top = above
    left = sp.left
  } else if (sp.left + sp.width + GAP + w <= vw - EDGE) {
    left = sp.left + sp.width + GAP
    top = sp.top
  } else if (sp.left - GAP - w >= EDGE) {
    left = sp.left - GAP - w
    top = sp.top
  } else {
    top = vh - h - EDGE
    left = vw - w - EDGE
  }
  return {
    top: `${Math.round(clamp(top, EDGE, vh - h - EDGE))}px`,
    left: `${Math.round(clamp(left, EDGE, vw - w - EDGE))}px`
  }
}

function sameShape(a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

function frame() {
  raf = requestAnimationFrame(frame)
  const current = step.value
  if (!store.isActive || !current) return

  isMobile.value = window.innerWidth <= MOBILE_MAX
  const card = cardRef.value
  if (card) cardSize = { w: card.offsetWidth || cardSize.w, h: card.offsetHeight || cardSize.h }

  const root = findRoot()
  if (!root) return

  stepFrames += 1
  let el = findTarget(root, current.target)
  let isWaiting = false

  if (!el) {
    if (current.optional) {
      if (stepFrames > OPTIONAL_GRACE_FRAMES) store.skipCurrent()
      return
    }
    isWaiting = true
    el = current.waitFor ? findTarget(root, current.waitFor) : null
  }

  if (waiting.value !== isWaiting) waiting.value = isWaiting

  const scrollKey = `${store.activeKey}:${store.stepIndex}:${isWaiting ? 'wait' : 'target'}`
  if (el && scrollKey !== scrolledFor) {
    scrolledFor = scrollKey
    bringIntoView(el)
  }

  let nextSpot = null
  if (el) {
    const rect = el.getBoundingClientRect()
    nextSpot = {
      top: Math.round(rect.top - SPOT_PAD),
      left: Math.round(rect.left - SPOT_PAD),
      width: Math.round(rect.width + SPOT_PAD * 2),
      height: Math.round(rect.height + SPOT_PAD * 2)
    }
  }
  if (!sameShape(spot.value, nextSpot)) spot.value = nextSpot

  const nextStyle = placeCard(nextSpot)
  if (!sameShape(cardStyle.value, nextStyle)) cardStyle.value = nextStyle
}

function skip() {
  store.end('skipped')
}

function turnOffGuides() {
  store.disableAll()
  store.end('skipped')
}

function onCardKeydown(event) {
  if (event.key !== 'Escape') return
  event.preventDefault()
  event.stopPropagation()
  skip()
}

function onSubmit(event) {
  if (!store.isActive) return
  const root = findRoot()
  if (root && event.target instanceof Node && root.contains(event.target)) store.end('completed')
}

watch(
  () => store.isActive,
  (active) => {
    if (active) {
      returnFocusEl = document.activeElement
      scrolledFor = ''
      stepFrames = 0
      spot.value = null
      cardStyle.value = {}
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(frame)
      document.addEventListener('submit', onSubmit, true)
      nextTick(() => nextBtnRef.value?.focus({ preventScroll: true }))
      return
    }
    cancelAnimationFrame(raf)
    cancelAnimationFrame(scrollRaf)
    document.removeEventListener('submit', onSubmit, true)
    spot.value = null
    waiting.value = false
    const el = returnFocusEl
    returnFocusEl = null
    if (el?.isConnected && typeof el.focus === 'function') el.focus({ preventScroll: true })
  }
)

watch(
  () => store.stepIndex,
  () => {
    stepFrames = 0
    nextTick(() => {
      if (!store.isActive) return
      const active = document.activeElement
      if (!active || active === document.body) nextBtnRef.value?.focus({ preventScroll: true })
    })
  }
)

onBeforeUnmount(() => {
  cancelAnimationFrame(raf)
  cancelAnimationFrame(scrollRaf)
  document.removeEventListener('submit', onSubmit, true)
})
</script>

<style scoped>
.help-tour-spot {
  box-shadow:
    0 0 0 2px currentColor,
    0 0 0 9999px rgba(17, 24, 39, 0.42);
  transition:
    top 0.25s ease,
    left 0.25s ease,
    width 0.25s ease,
    height 0.25s ease;
}
:global(.dark) .help-tour-spot {
  box-shadow:
    0 0 0 2px currentColor,
    0 0 0 9999px rgba(0, 0, 0, 0.6);
}

.help-tour-dim {
  background: rgba(17, 24, 39, 0.42);
}
:global(.dark) .help-tour-dim {
  background: rgba(0, 0, 0, 0.6);
}

.help-tour-card-enter-active,
.help-tour-card-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.help-tour-card-enter-from,
.help-tour-card-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@media (prefers-reduced-motion: reduce) {
  .help-tour-spot,
  .help-tour-card-enter-active,
  .help-tour-card-leave-active {
    transition: none;
  }
}
</style>
